import React, { useState } from 'react';
import { Auth } from "aws-amplify";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { login } from "../action/auth";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, Person, Business, Security, SupervisedUserCircle } from '@mui/icons-material';
import AuthLayout from './AuthLayout';

const UserTypeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px',
  marginBottom: '28px',
}));

const TypeCard = styled(Box)(({ theme, selected }) => ({
  padding: '16px',
  borderRadius: '16px',
  border: selected ? '2px solid var(--artemy-accent)' : '1px solid var(--artemy-border)',
  background: selected ? 'rgba(61, 127, 255, 0.08)' : 'var(--artemy-bg-elevated)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  '&:hover': {
    borderColor: selected ? 'var(--artemy-accent)' : 'var(--artemy-border-bright)',
    transform: 'translateY(-2px)',
  }
}));

const IconWrapper = styled(Box)(({ selected }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected ? 'var(--artemy-accent)' : 'rgba(139, 150, 180, 0.1)',
  color: selected ? '#fff' : 'var(--artemy-text-secondary)',
  marginBottom: '4px',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-dim)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  marginBottom: '16px',
});

const USER_TYPES = {
  candidate: { value: 'candidate', label: 'Talent', icon: <Person fontSize="small" />, redirects: { profile0: '/Createyourprofile', default: '/control-plane' }, errorMessage: 'User not found as talent.' },
  manager: { value: 'manager', label: 'Manager', icon: <Business fontSize="small" />, redirects: { profile0: '/client-profile', default: '/control-plane' }, errorMessage: 'User not found as manager.' },
  stakeholder: { value: 'stakeholder', label: 'Stakeholder', icon: <Security fontSize="small" />, redirects: { profile0: '/stakeholder-profile', default: '/StakeholderDashboard' }, errorMessage: 'User not found as Stakeholder.' },
  customer: { value: 'customer', label: 'Admin', icon: <SupervisedUserCircle fontSize="small" />, redirects: { profile0: '/customer-profile', default: '/subscriptions' }, errorMessage: 'User not found as Client-Admin.' }
};

const Login = () => {
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    username: '',
    password: '',
    table: '',
    showPassword: false
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (!formState.table) return 'Please select a user type!';
    if (!formState.username) return 'Please enter an email address!';
    if (!formState.username.toLowerCase().match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) return 'Please enter a valid email!';
    if (!formState.password) return 'Please enter a password!';
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError(null);

    try {
      await Auth.signIn(formState.username, formState.password);
      const userTypeCfg = USER_TYPES[formState.table];

      try {
        const data = await dispatch(login(formState.username, formState.table));
        const redirectPath = (data[0].profile === "0" || data[0].profile === 0)
          ? userTypeCfg.redirects.profile0
          : userTypeCfg.redirects.default;
        window.location.href = redirectPath;
      } catch (err) {
        setError(userTypeCfg.errorMessage);
      }
    } catch (error) {
      setError(error.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const selectUserType = (type) => {
    setFormState(prev => ({ ...prev, table: type }));
    setError(null);
  };

  return (
    <AuthLayout 
      title="Welcome Back" 
      subtitle="Enter your details to access your account"
    >
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '12px', bgcolor: 'rgba(255, 77, 106, 0.1)', color: 'var(--artemy-red)', border: '1px solid rgba(255, 77, 106, 0.2)' }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ color: 'var(--artemy-text-dim)', mb: 1.5, ml: 0.5, fontWeight: 500 }}>
          Select Role
        </Typography>
        <UserTypeGrid>
          {Object.entries(USER_TYPES).map(([key, type]) => (
            <TypeCard 
              key={key} 
              selected={formState.table === type.value}
              onClick={() => selectUserType(type.value)}
            >
              <IconWrapper selected={formState.table === type.value}>
                {type.icon}
              </IconWrapper>
              <Typography variant="body2" sx={{ fontWeight: 600, color: formState.table === type.value ? '#fff' : 'var(--artemy-text-primary)' }}>
                {type.label}
              </Typography>
            </TypeCard>
          ))}
        </UserTypeGrid>

        <StyledTextField
          fullWidth
          label="Email Address"
          name="username"
          value={formState.username}
          onChange={handleChange}
          required
          autoComplete="email"
        />

        <StyledTextField
          fullWidth
          label="Password"
          name="password"
          type={formState.showPassword ? 'text' : 'password'}
          value={formState.password}
          onChange={handleChange}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setFormState(p => ({ ...p, showPassword: !p.showPassword }))} edge="end" sx={{ color: 'var(--artemy-text-dim)' }}>
                  {formState.showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Link to="/forgotpassword" style={{ color: 'var(--artemy-accent)', textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 700,
            bgcolor: 'var(--artemy-accent) !important',
            boxShadow: '0 8px 24px rgba(61, 127, 255, 0.25)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 30px rgba(61, 127, 255, 0.35)',
            }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'var(--artemy-text-secondary)' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--artemy-accent)', textDecoration: 'none', fontWeight: 700 }}>
              Join now
            </Link>
          </Typography>
        </Box>
      </form>
    </AuthLayout>
  );
};

export default Login;